import React, { useEffect, useRef, useState } from 'react';
import { Loader2, RefreshCw, AlertCircle, Check, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent as UntypedPopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { iconDataUri } from '@/lib/sitePages';
import { formatMoney } from '@/config/business';

// ─── The pieces every Business screen is made of ─────────────────────────────
// A company's brand is drawn in its own colours and fonts, inside BuildersLab's
// dark tiles. The logo mark is the model's SVG, already sanitised on the
// server, and it is still only ever shown through <img>, where no script in it
// could run.

// shadcn's forwardRef components carry no prop types under checkJs, so every
// use of one is a type error (the baseline has dozens). Typed as any here
// rather than adding to that count.
const PopoverContent = /** @type {any} */ (UntypedPopoverContent);

const loadedFonts = new Set();

/** Load the brand's Google Fonts once, so the card, the ads and the posts wear them. */
export function useBrandFonts(fonts) {
  const heading = fonts?.heading;
  const body = fonts?.body;
  useEffect(() => {
    const wanted = [heading, body].filter(f => f && /^[A-Za-z0-9 ]{2,40}$/.test(f) && !loadedFonts.has(f));
    if (!wanted.length) return;
    wanted.forEach(f => loadedFonts.add(f));
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?${wanted.map(f => `family=${f.replace(/ /g, '+')}:wght@400;600;700;800`).join('&')}&display=swap`;
    document.head.appendChild(link);
  }, [heading, body]);
}

/** The colour with a role, or the nth as a fallback. */
export function roleColour(palette = [], role, fallbackIndex = 0, fallback = '#1C2433') {
  return palette.find(c => c.role === role)?.hex || palette[fallbackIndex]?.hex || fallback;
}

/** Black or white, whichever reads on `hex`. */
export function inkOn(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ''));
  if (!m) return '#ffffff';
  const n = parseInt(m[1], 16);
  const lum = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return lum > 0.6 ? '#111111' : '#ffffff';
}

/**
 * The mark in one colour. It is drawn in the brand's own colours, so on the
 * primary colour (the card's front, a dark tile) its main shape vanishes and
 * only the accent shows. Recoloured in the SVG itself rather than with a CSS
 * filter, because the print PDF is drawn by html2canvas, which ignores filters.
 */
export function monoMark(svg, colour) {
  return String(svg || '')
    .replace(/\b(fill|stroke)="(?!none\b)[^"]*"/gi, `$1="${colour}"`)
    .replace(/\b(fill|stroke)\s*:\s*(?!none\b)[^;"']+/gi, `$1:${colour}`)
    // A shape with no fill of its own is drawn black; give it the colour too.
    .replace(/<svg\b/i, `<svg fill="${colour}"`);
}

export function Mark({ svg, name = '', className = 'w-10 h-10', rounded = 'rounded-xl', colour = '' }) {
  const src = iconDataUri(colour ? monoMark(svg, colour) : svg);
  if (!src) {
    return (
      <span className={`${className} ${rounded} bg-zinc-800 text-white font-bold grid place-items-center shrink-0`} aria-hidden="true">
        {(String(name).trim()[0] || '?').toUpperCase()}
      </span>
    );
  }
  return <img src={src} alt={name ? `${name} logo` : ''} className={`${className} ${rounded} object-contain shrink-0`} />;
}

const STATUS_STYLE = {
  pending: { dot: 'bg-zinc-600', text: 'text-zinc-500', label: 'Waiting' },
  working: { dot: 'bg-orange-500 animate-pulse', text: 'text-orange-400', label: 'Working' },
  ready: { dot: 'bg-emerald-500', text: 'text-emerald-400', label: 'Ready' },
  failed: { dot: 'bg-red-500', text: 'text-red-400', label: 'Not made' },
};

export function StatusPill({ status = 'pending', label = '' }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} aria-hidden="true" />
      {label || s.label}
    </span>
  );
}

export function Swatches({ palette = [], size = 'h-10' }) {
  return (
    <div className="grid grid-cols-5 gap-1.5">
      {palette.map(c => (
        <div key={`${c.hex}-${c.name}`} className="min-w-0">
          <div className={`${size} rounded-lg border border-black/10`} style={{ background: c.hex }} title={`${c.name} ${c.hex}`} />
          <p className="mt-1 text-[10px] font-semibold text-zinc-300 truncate">{c.name}</p>
          <p className="text-[10px] text-zinc-500 font-mono uppercase">{c.hex}</p>
        </div>
      ))}
    </div>
  );
}

/**
 * "Ask for a change" on any part (§2.4): the founder's words go to the model
 * with the current version, and the old one stays on screen until the new one
 * lands.
 */
export function ChangeButton({ label = 'Ask for a change', placeholder = 'e.g. make the logo bolder', onSubmit, busy = false, compact = false }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const send = async (e) => {
    e?.preventDefault?.();
    try {
      await onSubmit(text.trim());
      setOpen(false);
      setText('');
    } catch (err) {
      toast.error(err.message || 'That change could not be started.');
    }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" disabled={busy}
          className={`inline-flex items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-900 text-zinc-300 hover:text-white hover:border-zinc-600 transition-colors disabled:opacity-50 ${compact ? 'px-2.5 py-1 text-[11px]' : 'px-3 py-1.5 text-xs font-semibold'}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
          {busy ? 'Working…' : label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 bg-zinc-950 border-zinc-800 text-zinc-200 p-3">
        <form onSubmit={send} className="space-y-2">
          <label className="text-xs font-semibold text-white" htmlFor="bl-change">What should change?</label>
          <textarea id="bl-change" value={text} onChange={e => setText(e.target.value)} rows={3} maxLength={400}
            placeholder={placeholder}
            className="w-full resize-none rounded-xl bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-zinc-600" />
          <div className="flex items-center justify-between gap-2">
            <button type="button" onClick={() => onSubmit('').then(() => setOpen(false)).catch(err => toast.error(err.message))}
              className="text-[11px] text-zinc-500 hover:text-zinc-300 inline-flex items-center gap-1">
              <RefreshCw className="w-3 h-3" /> Just try another
            </button>
            <button type="submit" disabled={!text.trim()}
              className="rounded-full bg-orange-500 text-ink text-xs font-bold px-3 py-1.5 disabled:opacity-40">
              Make the change
            </button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}

/** A part that failed: what happened, and "Try again". */
export function FailedPart({ error = '', onRetry = null, busy = false }) {
  return (
    <div className="flex flex-col items-start gap-2 text-sm">
      <p className="flex items-start gap-2 text-zinc-400"><AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />{error || 'This part could not be made.'}</p>
      {onRetry && (
        <button type="button" onClick={onRetry} disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-full bg-zinc-800 hover:bg-zinc-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Try again
        </button>
      )}
    </div>
  );
}

// ── The business card ─────────────────────────────────────────────────────────

/**
 * One side of the card, drawn at `widthPx` wide in the card's 85×55 mm
 * proportion (plus the 3 mm bleed when `bleed`). Every size is relative to
 * the width, so the same component is the screen preview and the print file.
 */
export function CardSide({ card, side = 'front', widthPx = 340, bleed = false }) {
  useBrandFonts(card?.fonts);
  const palette = card?.palette || [];
  const primary = roleColour(palette, 'primary', 0);
  const bg = roleColour(palette, 'background', 2, '#F6F3EE');
  const accent = roleColour(palette, 'accent', 4, '#D9622B');
  const wMm = 85 + (bleed ? 6 : 0);
  const hMm = 55 + (bleed ? 6 : 0);
  const u = widthPx / wMm;              // px per mm
  const pad = (bleed ? 3 : 0) * u + 5 * u;
  const heading = card?.fonts?.heading ? `'${card.fonts.heading}', Georgia, serif` : 'Georgia, serif';
  const body = card?.fonts?.body ? `'${card.fonts.body}', system-ui, sans-serif` : 'system-ui, sans-serif';
  /** @type {any} */
  const style = { width: widthPx, height: hMm * u, fontFamily: body, boxSizing: 'border-box' };

  if (side === 'front') {
    return (
      <div style={{ ...style, background: primary, color: inkOn(primary), padding: pad, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <img src={iconDataUri(monoMark(card?.markSvg, inkOn(primary)))} alt="" style={{ width: 13 * u, height: 13 * u, objectFit: 'contain' }} />
        <div>
          <div style={{ fontFamily: heading, fontSize: 6.2 * u, fontWeight: 700, lineHeight: 1.05, letterSpacing: '-0.01em' }}>{card?.name}</div>
          {card?.tagline && <div style={{ marginTop: 1.4 * u, fontSize: 2.5 * u, opacity: 0.8 }}>{card.tagline}</div>}
        </div>
      </div>
    );
  }
  const person = card?.person || {};
  const line = (text) => (text ? <div style={{ fontSize: 2.6 * u, lineHeight: 1.55 }}>{text}</div> : null);
  return (
    <div style={{ ...style, background: bg, color: inkOn(bg), padding: pad, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      <div>
        <div style={{ fontFamily: heading, fontSize: 4.4 * u, fontWeight: 700 }}>{person.name || 'Your name'}</div>
        <div style={{ fontSize: 2.4 * u, color: accent, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 0.6 * u }}>{person.role || 'Founder'}</div>
      </div>
      <div>
        {line(person.phone)}
        {line(person.email)}
        {line(card?.website)}
        {line(card?.city)}
      </div>
    </div>
  );
}

// ── An ad, at 1080×1350 ───────────────────────────────────────────────────────

/**
 * Instagram's 4:5 ad, laid out at its real size and scaled to fit: the screen
 * preview and the exported file are the same drawing (§2.11).
 */
export function AdCreative({ ad, brand, name, width = 270, exportRef = null }) {
  useBrandFonts(brand?.fonts);
  const palette = brand?.palette || [];
  const primary = roleColour(palette, 'primary', 0);
  const accent = roleColour(palette, 'accent', 4, '#D9622B');
  const bg = roleColour(palette, 'background', 2, '#F6F3EE');
  const scale = width / 1080;
  const heading = brand?.fonts?.heading ? `'${brand.fonts.heading}', Georgia, serif` : 'Georgia, serif';
  const body = brand?.fonts?.body ? `'${brand.fonts.body}', system-ui, sans-serif` : 'system-ui, sans-serif';
  return (
    <div style={{ width, height: 1350 * scale, overflow: 'hidden', borderRadius: 12 }} className="shrink-0">
      <div ref={exportRef} style={{ width: 1080, height: 1350, transform: `scale(${scale})`, transformOrigin: 'top left', position: 'relative', background: bg, fontFamily: body }}>
        {ad?.image
          ? <img src={ad.image} alt="" crossOrigin="anonymous" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          : <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(circle at 30% 30%, ${accent}55, transparent 60%), ${primary}` }} />}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.72) 100%)' }} />
        <div style={{ position: 'absolute', top: 56, left: 56, right: 56, display: 'flex', alignItems: 'center', gap: 20 }}>
          <img src={iconDataUri(monoMark(brand?.markSvg, '#ffffff'))} alt="" style={{ width: 84, height: 84, objectFit: 'contain' }} />
          <span style={{ color: '#fff', fontFamily: heading, fontSize: 40, fontWeight: 700, textShadow: '0 2px 12px rgba(0,0,0,0.35)' }}>{name}</span>
          {ad?.badge && <span style={{ marginLeft: 'auto', background: accent, color: inkOn(accent), fontSize: 28, fontWeight: 800, padding: '12px 24px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{ad.badge}</span>}
        </div>
        <div style={{ position: 'absolute', left: 56, right: 56, bottom: 64, color: '#fff' }}>
          <div style={{ fontFamily: heading, fontSize: 84, fontWeight: 800, lineHeight: 1.02, letterSpacing: '-0.02em' }}>{ad?.headline}</div>
          <div style={{ marginTop: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24 }}>
            <div>
              {ad?.product && <div style={{ fontSize: 34, opacity: 0.9 }}>{ad.product}</div>}
              {ad?.price && <div style={{ fontSize: 52, fontWeight: 800, marginTop: 6 }}>{ad.price}</div>}
            </div>
            <span style={{ background: '#fff', color: '#111', fontSize: 34, fontWeight: 800, padding: '22px 36px', borderRadius: 999, whiteSpace: 'nowrap' }}>{ad?.cta || 'Order on WhatsApp'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** One launch post as it shows in the Instagram grid. */
export function PostTile({ post, brand, name }) {
  const palette = brand?.palette || [];
  const colours = [roleColour(palette, 'primary', 0), roleColour(palette, 'accent', 4, '#D9622B'), roleColour(palette, 'secondary', 1, '#3B4A63')];
  const tint = colours[(post?.headline?.length || 0) % colours.length];
  const heading = brand?.fonts?.heading ? `'${brand.fonts.heading}', Georgia, serif` : 'Georgia, serif';
  return (
    <div className="relative aspect-square overflow-hidden rounded-md" style={{ background: tint }}>
      {post?.image && <img src={post.image} alt="" className="absolute inset-0 w-full h-full object-cover" />}
      {!post?.image && post?.visual === 'pattern' && (
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: `repeating-linear-gradient(45deg, ${inkOn(tint)}22 0 6px, transparent 6px 14px)` }} />
      )}
      {!post?.image && (
        <div className="absolute inset-0 p-2 flex items-end">
          <p className="text-[11px] leading-tight font-bold line-clamp-3" style={{ color: inkOn(tint), fontFamily: heading }}>{post?.headline || name}</p>
        </div>
      )}
    </div>
  );
}

/** The campaign line under the ads: "₦5,000 a day for 7 days". */
export function campaignLine(c) {
  if (!c) return '';
  return c.budgetPerDay ? `${formatMoney(c.budgetPerDay, c.currency)} a day for ${c.days} days` : `${c.days} days`;
}

/** Copy text, with a tick for a moment. */
export function CopyButton({ text, label = 'Copy', className = '' }) {
  const [done, setDone] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <button type="button" className={`inline-flex items-center gap-1 text-[11px] font-semibold text-zinc-400 hover:text-white ${className}`}
      onClick={async () => {
        try { await navigator.clipboard.writeText(String(text || '')); setDone(true); timer.current = setTimeout(() => setDone(false), 1500); } catch { toast.error('Could not copy.'); }
      }}>
      {done ? <Check className="w-3 h-3 text-emerald-400" /> : null}{done ? 'Copied' : label}
    </button>
  );
}
